/**
 * Questo hook personalizzato mi serve per la sessione.
 * Così posso gestire tutto quello che riguarda la sessione qui dentro
 * E ritorno dall'hook useSession le funzioni che mi servono
 *
 */
import { IStartSessionProps, IStopSessionProps } from "core/interfaces/util/ISession";
import { browserName } from "react-device-detect";

import moment from "moment";
import { message } from "antd";
import SessionRepository from "core/data/SessionRepository";
import { resetSession,  setSession } from "core/redux/features/session/sessionSlice";
import { useAppDispatch } from "core/redux/store/hooks";
import { getReduxSession } from "./ReduxUtils";
import { languageInUse } from "utils/i18n";
import { ISessionStateData } from "core/redux/features/session/sessionSliceInterfaces";


// Metodo che fa la richiesta ai servizi di creare una nuova sessione e mi faccio tornare la sessione
const InitSession = async (culture: string) => {
	const Browser = browserName;
	const session = await SessionRepository.createSession({ Browser, culture: culture });
	return session;
};

// Meotodo che fa partire il timer effettivo, quindi chiama il servizio e cambia la data di start della sessione
const StartSession = async (props: IStartSessionProps) => {
	const result = await SessionRepository.StartSession(props);
	return result;
};

// Meotodo che stoppa il timer effettivo, quindi chiama il servizio e cambia la data di end della sessione
const StopSession = async (props: IStopSessionProps) => {
	const result = await SessionRepository.EndSession(props);
	return result;
};

const useSession = (session: ISessionStateData) => {
	// const session = useAppSelector(selectSession);
	const dispatch = useAppDispatch();

	// funzione dell'hook che mi chiama InitSession per creare la sessione nel database tramite servizi
	const initSession = async () => {
		// se non esiste una sessione creo la nuova sessione
		if (!session.sessione) {
			const initSession = await InitSession(languageInUse);
			// e la setto per lo stato
			dispatch(setSession(getReduxSession(initSession)));
		}
	};

	// funzione dell'hook che mi chiama StartSession per dire che effettivamente la sessione è partita in questo momento e la setto sul db tramite servizi
	const startSession = async (step: number) => {
		if (session.sessione) {
			const DataInizioSession = moment().toISOString();

			const starteSession = await StartSession({
				Id: session?.sessione?.idSessione!,
				DataInizio: DataInizioSession,
				Step: step,
				culture: languageInUse,
			});

			dispatch(setSession(getReduxSession(starteSession)));
		}
	};

	/**
	 * Metodo che gestisce la fine della sessione
	 * Chiama il servizio per settare la data di fine della sessione nel database tramite servizi
	 * Se la sessione e' finita normalmente, cioè quando l'utente ha completato tutto correttamente
	 * setta la sessione ad undefined
	 * In caso sia finita perché é finito il timer scrive un messaggio di sessione scaduta e ricarica la pagina
	 * non permettendo di cliccare altro mettendo il loading block che ho messo sull'index
	 */
	const OnFinishSession = async (step: number, completed: boolean, forced?: boolean) => {
		if (session.sessione) {
			const DataFineSession = moment().toISOString();

			await StopSession({
				Id: session.sessione!.idSessione,
				DataFine: DataFineSession,
				Completed: completed,
				Step: step,
				culture: languageInUse,
			});

			if (!completed && !forced) {
				message.info("La sessione è scaduta. Tornerai all'inizio della registrazione...");

				const loadingBlock = document.getElementById("loading");
				if (loadingBlock) loadingBlock.style.display = "block";
				dispatch(resetSession());
				// setTimeout(() => {
				// 	const { GoToPage } = NavigationManager(session);
				// 	GoToPage(PagesEnum.ReservationForm, formStep.Home, { backNavAllowed: true, reset: true });
				// }, 2000);
			} else {
				dispatch(resetSession());
			}
		}
	};

	// Metodo per mandare la richiesta di fine sessione quando l'utente chiude la tab
	const OnCloseTab = async (step: number) => {
		if (session.sessione) {
			const DataFineSession = moment().toISOString();

			const data: IStopSessionProps = {
				Id: session.sessione!.idSessione,
				DataFine: DataFineSession,
				Completed: false,
				Step: step,
				culture: languageInUse,
			};

			await StopSession(data);
			dispatch(resetSession());
		}
	};

	// l'hook torna un oggetto con i metodo che uso e con le proprietà che mi servono
	return { session, initSession, startSession, OnFinishSession, OnCloseTab };
};

export default useSession;
